export type VoiceScreen =
  | "welcome"
  | "login"
  | "otp"
  | "home"
  | "profile"
  | "requests"
  | "request_detail"
  | "messages"
  | "account"
  | "new_order";

export const VOICE_SCREENS: readonly VoiceScreen[] = Object.freeze([
  "welcome",
  "login",
  "otp",
  "home",
  "profile",
  "requests",
  "request_detail",
  "messages",
  "account",
  "new_order",
]);

export const VOICE_EXPERT_V3_ROUTE = "/api/v1/customer/voice/v3/event";

export type VoiceOrderStep =
  | "item"
  | "count"
  | "area"
  | "more_items"
  | "more_items_confirm"
  | "complete";

export interface VoiceOrderState {
  readonly step: VoiceOrderStep;
  readonly itemLabel: string | null;
  readonly itemCount: number | null;
  readonly areaPerItemSqm: number | null;
  /** Null until a size is known; onsite means no made-up area is stored. */
  readonly measurementStatus: "provided" | "onsite" | null;
  readonly misunderstandingCount: number;
}

export interface VoiceSessionState {
  readonly version: 3;
  readonly screen: VoiceScreen;
  readonly screenVisit: number;
  readonly handledEventIds: readonly string[];
  readonly order: VoiceOrderState | null;
}

export type VoiceEvent =
  | { readonly id: string; readonly type: "screen_entered"; readonly screen: VoiceScreen }
  | { readonly id: string; readonly type: "replay_requested" }
  | { readonly id: string; readonly type: "order_answer"; readonly transcript: string };

export type VoiceCommand =
  | {
      readonly type: "speak";
      readonly key: string;
      readonly text: string;
      readonly required: true;
    }
  | {
      readonly type: "show_choices";
      readonly choices: readonly string[];
    };

export interface VoiceTransition {
  readonly state: VoiceSessionState;
  readonly commands: readonly VoiceCommand[];
}

export type VoiceApiCommand =
  | (Extract<VoiceCommand, { readonly type: "speak" }> & {
      readonly audioBase64: string | null;
      readonly audioMimeType: "audio/mpeg" | "audio/wav" | null;
    })
  | Extract<VoiceCommand, { readonly type: "show_choices" }>;

export interface VoiceEventRequest {
  readonly state?: VoiceSessionState;
  readonly event: VoiceEvent;
}

export interface VoiceEventResponse {
  readonly state: VoiceSessionState;
  readonly commands: readonly VoiceApiCommand[];
}

const SCREEN_GUIDES: Readonly<Record<VoiceScreen, string>> = Object.freeze({
  welcome: "سلام، به قالیشور خوش اومدید. قالیشور خودش قالیشویی نیست؛ درخواست شست‌وشوی فرش، موکت، مبل و سایر منسوجات شما رو ثبت می‌کنه و قالیشویی‌های مناسب رو بر اساس محدودهٔ خدمت‌رسانی، نوع خدمات، زمان، قیمت پیشنهادی، امتیاز و ضمانت بهتون معرفی می‌کنه. بسته به انتخاب شما، می‌تونید پیشنهادهای منتخب رو مقایسه کنید یا درخواستتون برای قالیشویی‌های واجد شرایط فرستاده بشه تا گزینهٔ مناسب پیدا بشه. از ثبت سفارش تا انتخاب قالیشویی و پیگیری، همراهتون هستیم. برای شروع، دکمهٔ «ورود و ثبت سفارش» رو انتخاب کنید.",
  login: "شماره موبایلتان را وارد کنید تا کد ورود برایتان ارسال شود.",
  otp: "کد شش‌رقمی پیامک‌شده را وارد کنید. اگر دستگاه اجازه بدهد، کد خودکار جای‌گذاری می‌شود.",
  home: "خوش آمدید. برای ثبت سفارش تازه، دکمه ثبت درخواست را بزنید.",
  profile: "اطلاعات تماس و نشانی را کامل کنید تا ثبت سفارش بدون توقف انجام شود.",
  requests: "در این صفحه می‌توانید وضعیت درخواست‌های خود را ببینید.",
  request_detail: "اینجا جزئیات و وضعیت سفارش شما نمایش داده می‌شود.",
  messages: "اینجا می‌توانید پیام‌های پشتیبانی و قالیشویی را دنبال کنید.",
  account: "در این صفحه می‌توانید مشخصات و نشانی خود را کامل کنید.",
  new_order: "من قدم‌به‌قدم سفارش را ثبت می‌کنم. اول بگویید چه چیزی برای شست‌وشو دارید.",
});

export function voiceGuideForScreen(screen: VoiceScreen): string {
  return SCREEN_GUIDES[screen];
}

const NUMBER_WORDS: Readonly<Record<string, number>> = Object.freeze({
  "یک": 1,
  "یه": 1,
  "دو": 2,
  "سه": 3,
  "چهار": 4,
  "پنج": 5,
  "شش": 6,
  "هفت": 7,
  "هشت": 8,
  "نه": 9,
  "ده": 10,
  "یازده": 11,
  "دوازده": 12,
  "سیزده": 13,
  "چهارده": 14,
  "پانزده": 15,
  "شانزده": 16,
  "هفده": 17,
  "هجده": 18,
  "نوزده": 19,
  "بیست": 20,
  "سی": 30,
  "چهل": 40,
  "پنجاه": 50,
  "شصت": 60,
  "هفتاد": 70,
  "هشتاد": 80,
  "نود": 90,
  "صد": 100,
});

const NUMBER_WORD_PATTERN = Object.keys(NUMBER_WORDS)
  .sort((a, b) => b.length - a.length)
  .join("|");
const NUMBER_EXPRESSION = `(?:\\d+(?:[./]\\d+)?|${NUMBER_WORD_PATTERN})(?:\\s+و\\s+(?:${NUMBER_WORD_PATTERN})){0,2}`;

const ITEM_LABELS: readonly { readonly pattern: RegExp; readonly label: string }[] = Object.freeze([
  { pattern: /فرش\s+ماشینی/u, label: "فرش ماشینی" },
  { pattern: /فرش\s+(?:دست[‌ -]?بافت|دستباف)/u, label: "فرش دست‌بافت" },
  { pattern: /موکِ?ت/u, label: "موکت" },
  { pattern: /مبل(?:مان)?/u, label: "مبل" },
  { pattern: /پرده/u, label: "پرده" },
  { pattern: /تشک/u, label: "تشک" },
  { pattern: /پتو/u, label: "پتو" },
  { pattern: /گلیم/u, label: "گلیم" },
  { pattern: /گبه/u, label: "گبه" },
  { pattern: /فرش/u, label: "فرش" },
]);

export const ORDER_AREA_CHOICES = Object.freeze([
  "۶ متر",
  "۹ متر",
  "۱۲ متر",
  "۱۵ متر",
  "اندازه‌گیری در محل",
] as const);

function normalize(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/[۰-۹]/g, (digit) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(digit)))
    .replace(/[٠-٩]/g, (digit) => String("٠١٢٣٤٥٦٧٨٩".indexOf(digit)))
    .replace(/[يى]/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/[\u200c\u200d]/g, " ")
    .replace(/[،؛؟?!]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function wordNumber(value: string): number | null {
  const direct = NUMBER_WORDS[value];
  if (direct !== undefined) return direct;
  const parts = value.split(/\s+و\s+/u);
  if (parts.length < 2) return null;
  let total = 0;
  for (const part of parts) {
    const number = NUMBER_WORDS[part];
    if (number === undefined) return null;
    total += number;
  }
  return total > 0 && total <= 100 ? total : null;
}

function firstPositiveNumber(value: string): number | null {
  const numeric = value.match(/(?:^|\s)(\d+(?:[./]\d+)?)(?=\s|$)/u)?.[1];
  if (numeric) {
    const parsed = Number(numeric.replace("/", "."));
    if (Number.isFinite(parsed) && parsed > 0 && parsed <= 1000) return parsed;
  }
  const words = Object.keys(NUMBER_WORDS).sort((a, b) => b.length - a.length);
  const match = value.match(new RegExp(`(?:^|\\s)(${words.join("|")})(?=\\s|$)`, "u"));
  return match?.[1] ? wordNumber(match[1]) : null;
}

function numberExpressionValue(value: string): number | null {
  const normalized = normalize(value);
  const numeric = Number(normalized.replace("/", "."));
  if (Number.isFinite(numeric) && numeric > 0 && numeric <= 1000) return numeric;
  return wordNumber(normalized);
}

function contextualNumber(value: string, following: string): number | null {
  const match = value.match(new RegExp(`(${NUMBER_EXPRESSION})\\s*(?:${following})`, "u"));
  return match?.[1] ? numberExpressionValue(match[1]) : null;
}

function itemLabelFromAnswer(value: string): { readonly label: string; readonly index: number } | null {
  for (const item of ITEM_LABELS) {
    const match = item.pattern.exec(value);
    if (match?.index !== undefined) return { label: item.label, index: match.index };
  }
  return null;
}

export interface ParsedOrderItemAnswer {
  readonly itemLabel: string;
  readonly itemCount: number | null;
  readonly areaPerItemSqm: number | null;
}

export function parseOrderItemAnswer(transcript: string): ParsedOrderItemAnswer | null {
  const text = normalize(transcript);
  const item = itemLabelFromAnswer(text);
  if (!item) return null;
  const explicitCount = contextualNumber(text, "عدد|تخته|تا|دانه");
  const itemCount = explicitCount ?? firstPositiveNumber(text.slice(0, item.index));
  const dimension = text.match(new RegExp(`(${NUMBER_EXPRESSION})\\s*(?:در|×|x)\\s*(${NUMBER_EXPRESSION})`, "u"));
  const width = dimension?.[1] ? numberExpressionValue(dimension[1]) : null;
  const length = dimension?.[2] ? numberExpressionValue(dimension[2]) : null;
  const explicitArea = contextualNumber(text, "متر\\s*مربع|متری|متره|مترن|متر");
  const areaPerItemSqm = width && length
    ? Math.round(width * length * 100) / 100
    : explicitArea === null
      ? null
      : Math.round(explicitArea * 100) / 100;
  return {
    itemLabel: item.label,
    itemCount: itemCount !== null && Number.isInteger(itemCount) && itemCount <= 100 ? itemCount : null,
    areaPerItemSqm,
  };
}

export function preparePersianSpeechText(text: string): string {
  return text.replace(/مُ?وکِ?ت/gu, "موکِت");
}

export function parseAreaAnswer(transcript: string): number | null {
  const text = normalize(transcript)
    .replace(/متر\s*مربع/gu, "متر")
    .replace(/متری\b/gu, "متر")
    .replace(/متره\b|مترن\b/gu, "متر")
    .replace(/هردو(?:تا)?(?:شون|ش)?/gu, "هر کدام")
    .replace(/هر\s+دو(?:تا)?(?:شون|ش)?\b/gu, "هر کدام")
    .replace(/هرکدوم/gu, "هر کدوم");
  const dimension = text.match(/(.+?)\s*(?:در|×|x)\s*(.+?)(?:\s*متر)?$/u);
  if (dimension) {
    const width = firstPositiveNumber(dimension[1] ?? "");
    const length = firstPositiveNumber(dimension[2] ?? "");
    if (width && length) return Math.round(width * length * 100) / 100;
  }
  const number = firstPositiveNumber(text);
  return number === null ? null : Math.round(number * 100) / 100;
}

function parseCount(transcript: string): number | null {
  const number = firstPositiveNumber(normalize(transcript));
  return number !== null && Number.isInteger(number) && number <= 100 ? number : null;
}

function saidYes(transcript: string): boolean {
  return /^(?:بله|آره|اره|دارم|یکی دیگه|مورد دیگه)/u.test(normalize(transcript));
}

function saidNo(transcript: string): boolean {
  const answer = normalize(transcript);
  return (
    /(?:^|\s)(?:نه|خیر|نخیر|ندارم|تمام|تموم|تمومه|تمامه|کافیه)(?:\s|$)/u.test(answer) ||
    /(?:دیگه|دیگر|هیچ)\s+(?:چیزی|موردی)?\s*(?:ندارم|نیست)/u.test(answer) ||
    /(?:مورد|چیز)\s+(?:دیگه|دیگری|دیگر)\s+(?:ندارم|نیست)/u.test(answer) ||
    /^(?:همین|همین بود|همینه|فقط همین)(?:\s|$)/u.test(answer) ||
    /^(?:بریم(?:\s+(?:جلو|بعدی|مرحله\s+بعد))?|ادامه(?:\s+(?:بدیم|بده|ده))?|برو(?:\s+(?:جلو|بعدی|مرحله\s+بعد))?|مرحله\s+بعد|بعدی|ثبت(?:ش)?\s+کن|تکمیل(?:ش)?\s+کن)(?:\s+(?:لطفا|لطفاً|دیگه))?[.\s]*$/u.test(answer)
  );
}

function asksForOnsiteMeasurement(transcript: string): boolean {
  const answer = normalize(transcript);
  return answer === "اندازه گیری در محل" ||
    /(?:متراژ|اندازه).{0,18}(?:نمی\s*دانم|نمیدونم|نمی\s*دونم)/u.test(answer);
}

function emptyOrder(): VoiceOrderState {
  return {
    step: "item",
    itemLabel: null,
    itemCount: null,
    areaPerItemSqm: null,
    measurementStatus: null,
    misunderstandingCount: 0,
  };
}

export function createVoiceSession(screen: VoiceScreen = "login"): VoiceSessionState {
  return {
    version: 3,
    screen,
    screenVisit: 0,
    handledEventIds: [],
    order: screen === "new_order" ? emptyOrder() : null,
  };
}

export function isVoiceSessionState(value: unknown): value is VoiceSessionState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<VoiceSessionState>;
  if (
    candidate.version !== 3 ||
    !VOICE_SCREENS.includes(candidate.screen as VoiceScreen) ||
    !Number.isInteger(candidate.screenVisit) ||
    !Array.isArray(candidate.handledEventIds) ||
    candidate.handledEventIds.some((id) => typeof id !== "string" || id.length < 1 || id.length > 100)
  ) return false;
  if (candidate.order === null) return true;
  if (!candidate.order || typeof candidate.order !== "object") return false;
  const order = candidate.order as Partial<VoiceOrderState>;
  return (
    ["item", "count", "area", "more_items", "more_items_confirm", "complete"].includes(order.step ?? "") &&
    (order.itemLabel === null || typeof order.itemLabel === "string") &&
    (order.itemCount === null || (Number.isInteger(order.itemCount) && (order.itemCount ?? 0) > 0)) &&
    (order.areaPerItemSqm === null || (typeof order.areaPerItemSqm === "number" && order.areaPerItemSqm > 0)) &&
    (order.measurementStatus === undefined || order.measurementStatus === null ||
      order.measurementStatus === "provided" || order.measurementStatus === "onsite") &&
    Number.isInteger(order.misunderstandingCount) &&
    (order.misunderstandingCount ?? -1) >= 0
  );
}

function speak(key: string, text: string): VoiceCommand {
  return { type: "speak", key, text, required: true };
}

function remember(state: VoiceSessionState, eventId: string): VoiceSessionState {
  return {
    ...state,
    handledEventIds: [...state.handledEventIds.slice(-19), eventId],
  };
}

function currentPrompt(state: VoiceSessionState): VoiceCommand {
  const order = state.order;
  if (state.screen !== "new_order" || !order) return speak(`screen.${state.screen}`, voiceGuideForScreen(state.screen));
  if (order.step === "item") return speak("order.item", SCREEN_GUIDES.new_order);
  if (order.step === "count") return speak("order.count", `چند عدد ${order.itemLabel ?? "مورد"} دارید؟`);
  if (order.step === "area") return speak(
    "order.area",
    `متراژ هر ${order.itemLabel ?? "مورد"} چقدر است؟ یکی از گزینه‌ها را لمس کنید یا بگویید مثلاً دوازده متر یا سه در چهار.`,
  );
  if (order.step === "more_items") {
    return speak(
      "order.more_items",
      "اگر مورد دیگه‌ای مثل فرش، موکت، مبل، پرده، پتو یا تشک برای دریافت خدمات قالیشور دارید، بگید تا به همین سفارش اضافه کنم.",
    );
  }
  if (order.step === "more_items_confirm") {
    return speak(
      "order.more_items_confirm",
      "پس هیچ مورد دیگری برای دریافت خدمات قالیشور ندارید؟ اگر نه، برای تکمیل ثبت سفارش بریم مرحلهٔ بعد.",
    );
  }
  return speak(
    "order.complete",
    "اقلام و خدمات اصلی سفارش کامل شد. حالا می‌تونید قالیشویی رو با چهار معیار کلی انتخاب کنید: انتخاب متعادل، قیمت اقتصادی‌تر، تحویل سریع‌تر، یا کیفیت و تضمین بیشتر. در مرحلهٔ بعد گزینه‌ای رو انتخاب کنید که براتون مهم‌تره.",
  );
}

function promptCommands(state: VoiceSessionState): readonly VoiceCommand[] {
  const prompt = currentPrompt(state);
  if (state.screen === "new_order" && state.order?.step === "area") {
    return [prompt, { type: "show_choices", choices: ORDER_AREA_CHOICES }];
  }
  return [prompt];
}

export function transitionVoiceSession(state: VoiceSessionState, event: VoiceEvent): VoiceTransition {
  if (state.handledEventIds.includes(event.id)) return { state, commands: [] };
  let next = remember(state, event.id);

  if (event.type === "screen_entered") {
    next = {
      ...next,
      screen: event.screen,
      screenVisit: state.screenVisit + 1,
      order: event.screen === "new_order" ? state.order ?? emptyOrder() : null,
    };
    return { state: next, commands: promptCommands(next) };
  }

  if (event.type === "replay_requested") return { state: next, commands: promptCommands(next) };
  if (next.screen !== "new_order" || !next.order) return { state: next, commands: promptCommands(next) };

  const answer = normalize(event.transcript);
  const order = next.order;
  if (order.step === "item") {
    if (answer.length < 2) return { state: next, commands: [speak("order.item.retry", "نام مورد را بگویید؛ مثلاً فرش ماشینی.")] };
    const parsed = parseOrderItemAnswer(answer);
    if (!parsed) {
      return {
        state: next,
        commands: [speak("order.item.retry", "متوجه نوع مورد نشدم؛ مثلاً بگویید فرش ماشینی، موکت یا مبل.")],
      };
    }
    if (parsed?.itemCount && parsed.areaPerItemSqm) {
      next = {
        ...next,
        order: { ...order, step: "more_items", ...parsed, measurementStatus: "provided", misunderstandingCount: 0 },
      };
      return {
        state: next,
        commands: [speak(
          "order.item.complete.accepted",
          `${parsed.itemCount} عدد ${parsed.itemLabel}، هرکدام ${parsed.areaPerItemSqm} متر ثبت شد. مورد دیگری هم دارید؟`,
        )],
      };
    }
    next = {
      ...next,
      order: {
        ...order,
        step: parsed?.itemCount ? "area" : "count",
        itemLabel: parsed.itemLabel,
        itemCount: parsed?.itemCount ?? null,
        areaPerItemSqm: parsed?.areaPerItemSqm ?? null,
        measurementStatus: parsed?.areaPerItemSqm ? "provided" : null,
        misunderstandingCount: 0,
      },
    };
    return { state: next, commands: promptCommands(next) };
  }

  if (order.step === "count") {
    const parsed = parseOrderItemAnswer(`${order.itemLabel ?? ""} ${answer}`);
    const count = parsed?.itemCount ?? parseCount(answer);
    if (count === null) return { state: next, commands: [speak("order.count.retry", "فقط تعداد را بگویید؛ مثلاً دو عدد.")] };
    if (parsed?.areaPerItemSqm) {
      next = {
        ...next,
        order: {
          ...order,
          step: "more_items",
          itemCount: count,
          areaPerItemSqm: parsed.areaPerItemSqm,
          measurementStatus: "provided",
          misunderstandingCount: 0,
        },
      };
      return {
        state: next,
        commands: [speak(
          "order.count_and_area.accepted",
          `${count} عدد ${order.itemLabel ?? "مورد"}، هرکدام ${parsed.areaPerItemSqm} متر ثبت شد. مورد دیگری هم دارید؟`,
        )],
      };
    }
    next = {
      ...next,
      order: {
        ...order,
        step: order.areaPerItemSqm ? "more_items" : "area",
        itemCount: count,
        misunderstandingCount: 0,
      },
    };
    return { state: next, commands: promptCommands(next) };
  }

  if (order.step === "area") {
    const area = parseAreaAnswer(answer);
    if (area !== null) {
      next = {
        ...next,
        order: { ...order, step: "more_items", areaPerItemSqm: area, measurementStatus: "provided", misunderstandingCount: 0 },
      };
      return {
        state: next,
        commands: [speak("order.area.accepted", `متراژ هرکدام ${area} متر ثبت شد. مورد دیگری هم دارید؟`)],
      };
    }
    if (asksForOnsiteMeasurement(answer)) {
      return {
        state: {
          ...next,
          order: { ...order, step: "more_items", areaPerItemSqm: null, measurementStatus: "onsite", misunderstandingCount: 0 },
        },
        commands: [
          speak("order.area.onsite", "اندازه‌گیری هنگام تحویل ثبت شد. مورد دیگری هم دارید؟"),
        ],
      };
    }
    const attempts = Math.min(1, order.misunderstandingCount + 1);
    next = { ...next, order: { ...order, misunderstandingCount: attempts } };
    return {
      state: next,
      commands: [
        speak("order.area.choice", "یکی از متراژهای زیر را لمس کنید؛ اگر متراژ را نمی‌دانید، اندازه‌گیری در محل را انتخاب کنید."),
        { type: "show_choices", choices: ORDER_AREA_CHOICES },
      ],
    };
  }

  if (order.step === "more_items") {
    if (saidNo(answer)) {
      next = { ...next, order: { ...order, step: "more_items_confirm", misunderstandingCount: 0 } };
      return { state: next, commands: promptCommands(next) };
    }
    if (saidYes(answer)) {
      next = { ...next, order: emptyOrder() };
      return { state: next, commands: promptCommands(next) };
    }
    const parsed = parseOrderItemAnswer(answer);
    if (parsed) {
      next = {
        ...next,
        order: {
          ...emptyOrder(),
          ...parsed,
          measurementStatus: parsed.areaPerItemSqm ? "provided" : null,
          step: parsed.itemCount
            ? parsed.areaPerItemSqm
              ? "more_items"
              : "area"
            : "count",
        },
      };
      return { state: next, commands: promptCommands(next) };
    }
    return { state: next, commands: [speak("order.more_items.retry", "اگر مورد دیگری دارید بگویید بله؛ اگر ندارید بگویید نه.")] };
  }

  if (order.step === "more_items_confirm") {
    if (saidNo(answer)) {
      next = { ...next, order: { ...order, step: "complete", misunderstandingCount: 0 } };
    return { state: next, commands: promptCommands(next) };
    }
    if (saidYes(answer)) {
      next = { ...next, order: emptyOrder() };
      return { state: next, commands: promptCommands(next) };
    }
    const parsed = parseOrderItemAnswer(answer);
    if (parsed) {
      next = {
        ...next,
        order: {
          ...emptyOrder(),
          ...parsed,
          measurementStatus: parsed.areaPerItemSqm ? "provided" : null,
          step: parsed.itemCount
            ? parsed.areaPerItemSqm
              ? "more_items"
              : "area"
            : "count",
        },
      };
      return { state: next, commands: promptCommands(next) };
    }
    return {
      state: next,
      commands: [speak("order.more_items_confirm.retry", "اگه مورد دیگه‌ای ندارید بگید نه؛ وگرنه مورد بعدی رو بگید.")],
    };
  }

  return { state: next, commands: promptCommands(next) };
}
